import React from 'react';

import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { normalizeSectionSpec, SectionContainerBaseProps } from '~settings-coordinator/SettingsSectionList';
import { incrementRouterSpaceReactKey } from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';

import { getAppStore } from '../../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../../codegen/Localize';
import WebHttpClient from '../../../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '../../../../sessionManagement/SessionManagementService';
import { SettingsFormikContainer } from '../../../components/common/SettingsFormikContainer';
import { GeneralServerSettingsSubspaceState } from '../../../components/subspaces/ServerSettingsSpace/GeneralServerSettingsSubspace';
import { ResetServerSettingsRequest } from '../../../ServerApi/requests/serverSettings/ResetServerSettingsRequest';
import { UpdateBooleanServerSettingNames } from '../../../ServerApi/requests/serverSettings/UpdateBooleanServerSettingRequest';
import { UpdateStringServerSettingNames } from '../../../ServerApi/requests/serverSettings/UpdateStringServerSettingRequest';
import { IEditableServerSettings } from '../../../ServerApi/types/IEditableServerSettings';
import { IServerTimestamps, ServerSettingsInstance } from '../../../services/ServerSettings';

export type ReactServerSettingsSubFormikProps = {
  settings: GeneralServerSettingsSubspaceState;
}

export type ServerSettingsProps<T extends keyof IEditableServerSettings = keyof IEditableServerSettings> = {
  settings: Pick<IEditableServerSettings, T>;
  serverTimestamps?: IServerTimestamps;
};

const serverSettingsSections = [
  'ServerStartPageSection',
  'ActiveDirectorySyncSection',
  'ServerGuestAccessSection',
  'EmbeddedCredentialsSection',
  'SignInNoteSection',
  'RecommendationsTrainingSection',
  'ConnectedClientsSettingSection',
  'LanguageAndLocaleSection',
  'SaveCredentialsSection',
  'LinkedTaskServerSettingSection',
  'FlowParametersServerSettingSection',
  'RunNowServerSettingSection',
  'ServerGroupAssertionsSection'
];

export function ReactServerSettings(props: ServerSettingsProps) {
  const initialServerSettings = React.useMemo(
    () => {
      return serverSettingsSections
        ?.map(normalizeSectionSpec)
        ?.reduce((settings, [Section]) => Section?.transformSettingsOnLoad?.(settings) ?? settings, props.settings)
        ?? props.settings;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverSettingsSections, props.settings]
  );
  const {localServerSettings, dialogHandler} = useResetToDefaultsHandler(initialServerSettings);
  const submitHandler = useSubmitHandler(serverSettingsSections, localServerSettings);
  return <SettingsFormikContainer
    formName={'ReactServerSettings'}
    sections={serverSettingsSections}
    onSubmit={submitHandler}
    onResetToDefaults={dialogHandler}
    enableReinitialize={true}
    {...props}
    settings={localServerSettings}
  />;
}

function useSubmitHandler(sections: SectionContainerBaseProps<ServerSettingsProps['settings']>['sections'],
                          previousServerSettings: IEditableServerSettings) {
  return React.useCallback(async (settings: ServerSettingsProps['settings']) => {
    const serverSettingsService = ServerSettingsInstance;
    const booleanNames = UpdateBooleanServerSettingNames;
    const stringNames = UpdateStringServerSettingNames;

    const embeddedCredentialsAndSchedulingPromise =
      serverSettingsService.updateBoolean(booleanNames.embeddedCredentialsEnabled, settings.embeddedCredentialsEnabled)
        .then(() => {
          // Since scheduling depends on embeddedCredentials being enabled, make sure embeddedCredentials
          // saves first and only save scheduling if embeddedCredentials is enabled
          if (settings.embeddedCredentialsEnabled) {
            return serverSettingsService.updateBoolean(booleanNames.schedulingEnabled, settings.schedulingEnabled);
          }
        });


    /* TODO: We really should discourage a multiple central places to hardcode specific settings like this. Settings
     * section specs should specify onSubmit callbacks, and containers should execute all of them here. This
     * keeps sections pluggable and modular without having to update multiple files to change things
     */
    const promises = [
      embeddedCredentialsAndSchedulingPromise,
      // serverSettingsService.updateBoolean(booleanNames.savedPasswordsEnabled, settings.savedPasswordsEnabled),
      // serverSettingsService.updateBoolean(booleanNames.savedAccessTokensEnabled, settings.savedAccessTokensEnabled),
      // serverSettingsService.updateString(stringNames.defaultLanguage, settings.defaultLanguage),
      // serverSettingsService.updateString(stringNames.defaultLocale, settings.defaultLocale),
      serverSettingsService.updateBoolean(booleanNames.refreshTokensEnabled, settings.refreshTokenEnabled),
      serverSettingsService.updateBoolean(booleanNames.personalSandboxEnabled, settings.personalSandboxEnabled),
      serverSettingsService.updateBoolean(booleanNames.personalSandboxByDefaultEnabled, settings.personalSandboxByDefaultEnabled),
      // serverSettingsService.updateString(stringNames.signInNoteText, settings.signInNoteText),
      // serverSettingsService.updateString(stringNames.signInNoteUrl, settings.signInNoteUrl)
    ];

    // promises.push(serverSettingsService
    //   .updateBoolean(booleanNames.scheduledActiveDirectorySyncEnabled, settings.scheduledActiveDirectorySyncEnabled));
    // promises.push(serverSettingsService
    //   .updateScheduleFrequency(UpdateScheduleFrequencyServerSettingNames.activeDirectorySyncScheduleFrequency,
    //     settings.activeDirectorySyncScheduleFrequency));
    // promises.push(serverSettingsService
    //   .updateScheduleFrequency(UpdateScheduleFrequencyServerSettingNames.recommendationsTrainerScheduleFrequency,
    //     settings.recommendationsTrainerScheduleFrequency));

    // if (settings.guestEnabled !== undefined) {
    //   promises.push(serverSettingsService.updateBoolean(booleanNames.guestEnabled, settings.guestEnabled));
    // }

    const perSectionSubmitPromises = sections
      ?.map(normalizeSectionSpec)
      ?.map(([Section]) => Section.onSubmit?.(settings))
      ?.filter(result => !!result);

    /**
     * TODO: Move all hard-coded promises into submit handlers in their corresponding sections, then remove the
     * (eventually) empty `promises` array
     */
    await Promise.all([
      ...promises,
      ...perSectionSubmitPromises
    ]);

    const isLangChanged = isLangOrLocaleChanged(previousServerSettings, settings);
    await hardReloadIfNeeded(isLangChanged);

  }, [sections, previousServerSettings]);
}

function useResetToDefaultsHandler(initialServerSettings: IEditableServerSettings) {
  const [localServerSettings, setLocalServerSettings] = React.useState(initialServerSettings);
  const dialogHandler = React.useCallback(() => {
      const dialogResult = openReactConfirmationDialog({
        title: Messages.resetServerSettings(),
        message: Messages.resetServerSettingsConfirmMessage(),
        actionName: Messages.resetServerSettingsConfirmButton(),
        isActionDelete: true,
        paramsForAction: null,
        dialogWidth: TabDialogWidth.width375px,
        action: async () => {
          try {
            await WebHttpClient.sendRequest(new ResetServerSettingsRequest());
            const newServerSettings: IEditableServerSettings = await ServerSettingsInstance.fetch();
            const isLangChanged = isLangOrLocaleChanged(localServerSettings, newServerSettings);
            setLocalServerSettings(newServerSettings);

            await hardReloadIfNeeded(isLangChanged);
          } catch (e) {
            await showResetErrorToast();
          }
        }
      }).result;

      return dialogResult;
    },
    [localServerSettings, setLocalServerSettings]);

  return {localServerSettings, dialogHandler};
}

function isLangOrLocaleChanged(oldServerSettings: IEditableServerSettings, newServerSettings: IEditableServerSettings): boolean {
  const langChanged   = oldServerSettings.defaultLanguage !== newServerSettings.defaultLanguage;
  const localeChanged = oldServerSettings.defaultLocale   !== newServerSettings.defaultLocale;
  return langChanged || localeChanged;
}

/**
 * TODO: This solution seems like it could be improved in the future
 * @param isLangChanged
 */
async function hardReloadIfNeeded(isLangChanged: boolean): Promise<any> {
  let hardReloadPromise: Promise<any>;
  if (isLangChanged) {
    await getSessionManagementServiceInstance().refreshSessionInfo();
    getAppStore().dispatch(incrementRouterSpaceReactKey());
  }
}

async function showResetErrorToast() {
  ToasterService.getInstance().pop({type: ToastType.Info, text: Messages.resetServerSettingsError()});
}
